import { useEffect, useState } from "react";

import ChatMarkdown from "../ChatMarkdown";
import { MorrowMascot } from "../brand/MorrowMascot";
import type { MorrowGreetingState } from "../../hooks/useMorrowGreeting";

/**
 * Renders the proactive "app speaks first" greeting on the empty-chat landing.
 *
 * While the server builds the user's profile we show an alive, on-brand
 * "getting to know you" state (a traveling ASCII pulse + calm, cycling status
 * lines) that comfortably fills the ~1–2 min wait. Once ready, we render the
 * server's markdown as the assistant's first message with the connected apps'
 * suggestions as clickable chips.
 *
 * This component is purely presentational — all fetch/poll/gate logic lives in
 * `useMorrowGreeting`. It only renders for the `loading` and `ready` states;
 * the caller shows the static landing for anything else.
 */
interface MorrowWelcomeGreetingProps {
  readonly state: MorrowGreetingState;
  /** Submits the picked text as the first user message (same path as static suggestions). */
  readonly onSelect: (text: string) => void;
}

// Calm, confident status lines that rotate every few seconds while we wait. Kept
// inline (not i18n) since they're a playful, brand-voiced loading flourish.
const LOADING_LINES = [
  "Reading your world…",
  "Connecting the dots across your apps…",
  "Learning how you work…",
  "Noticing what matters to you…",
  "Getting a feel for your week…",
  "Almost there…",
] as const;

const LINE_ROTATE_MS = 2800;
const PULSE_TICK_MS = 110;
const PULSE_WIDTH = 8;

/**
 * A calm comet traveling a dotted track, built from characters so it reads as a
 * quiet "thinking" signal rather than a busy spinner. Purely decorative.
 */
function AsciiPulse() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), PULSE_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const head = tick % PULSE_WIDTH;
  const chars = Array.from({ length: PULSE_WIDTH }, (_, i) => {
    const distance = Math.abs(i - head);
    if (distance === 0) return "●";
    if (distance === 1) return "◉";
    if (distance === 2) return "•";
    return "·";
  });

  return (
    <pre
      aria-hidden="true"
      className="select-none font-mono text-base leading-none text-muted-foreground/70 tracking-[0.15em]"
    >
      {chars.join(" ")}
    </pre>
  );
}

function GreetingLoading() {
  const [lineIndex, setLineIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setLineIndex((i) => (i + 1) % LOADING_LINES.length),
      LINE_ROTATE_MS,
    );
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className="pointer-events-auto mx-auto flex w-full max-w-lg flex-col items-center gap-6"
      role="status"
      aria-live="polite"
    >
      <MorrowMascot className="size-16 sm:size-20" />
      <AsciiPulse />
      <div className="flex min-h-[1.5rem] items-center justify-center text-center">
        {/* key swap re-triggers the fade so each line eases in as it rotates. */}
        <p
          key={lineIndex}
          className="morrow-greeting-line max-w-sm text-balance text-muted-foreground text-sm"
        >
          {LOADING_LINES[lineIndex]}
        </p>
      </div>
    </div>
  );
}

function GreetingReady({ state, onSelect }: MorrowWelcomeGreetingProps) {
  return (
    <div className="pointer-events-auto mx-auto flex w-full max-w-2xl flex-col gap-5">
      {/* The greeting reuses the app's assistant markdown so it reads as a real
          first message from Morrow, with the mascot standing in for an avatar. */}
      <div className="flex items-start gap-3">
        <MorrowMascot className="mt-0.5 size-8 sm:size-9" />
        <div className="min-w-0 flex-1">
          <ChatMarkdown text={state.greeting} cwd={undefined} />
        </div>
      </div>
      {state.suggestions.length > 0 ? (
        <div className="flex flex-wrap gap-2 ps-11">
          {state.suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => onSelect(suggestion)}
              className="button-glass inline-flex items-center gap-2 rounded-full border border-border/60 px-3.5 py-2 text-left text-sm font-medium transition-[transform,filter,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-border hover:brightness-[1.02] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {suggestion}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function MorrowWelcomeGreeting({ state, onSelect }: MorrowWelcomeGreetingProps) {
  if (state.status === "ready") {
    return <GreetingReady state={state} onSelect={onSelect} />;
  }
  if (state.status === "loading") {
    return <GreetingLoading />;
  }
  // `disabled` / `gaveup` render nothing — the caller shows the static landing.
  return null;
}
